/**
 * Entrar como outro usuario, e voltar.
 *
 * Guarda no localStorage quem esta por tras, para a faixa de aviso e o botao
 * de voltar sobreviverem a recarregar a pagina -- sem isso, quem atualizasse
 * a tela perderia a indicacao de que nao esta na propria conta, que e
 * exatamente a informacao que nao pode sumir.
 */
import { apiRequest } from '@/services/api/apiClient.js';
import { setAuth } from '@/services/api/auth.js';
import { useAuthStore } from '@/stores/authStore.js';
import { marcarPermissaoDesconfiavel } from '@/services/permissao.js';

const CHAVE_ATOR = 'personificando:ator';

/** Slug do admin no catálogo -- é para lá que a personificação leva. */
const SLUG_ADMIN = 'hliberty.admin';

/** O login de quem esta por tras, ou null quando nao ha personificacao. */
export function atorAtual() {
    return localStorage.getItem(CHAVE_ATOR) || null;
}

export function personificando() {
    return atorAtual() !== null;
}

/**
 * Assume a identidade de outro usuario.
 *
 * O backend decide se pode -- nunca o bypass, so empresa em comum, sem
 * cadeia. Aqui nao ha copia dessas regras de proposito: regra duplicada no
 * front vira regra que diverge do back e da a impressao de proteger.
 */
export async function entrarComo(idUsuario) {
    const resposta = await apiRequest({
        client: window.API_AUTH,
        method: 'post',
        url: `user/${idUsuario}/personificar`,
    });

    aplicar(resposta);

    // O ator sai do token novo, nao de quem clicou: se por algum motivo o
    // backend devolver 200 sem ter personificado, nao marcamos uma
    // personificacao que nao aconteceu.
    const ator = lerAtorDoToken(resposta?.access_token);

    if (ator) {
        localStorage.setItem(CHAVE_ATOR, ator);
    }

    await atualizarPermissoes();

    return resposta;
}

/**
 * Pede ao auth um ingresso de SSO para o admin, já na identidade assumida.
 *
 * Devolve a URL para onde navegar, ou `null` quando o alvo não tem acesso ao
 * admin nesta empresa (ou o admin não está no catálogo neste ambiente) -- aí
 * quem chamou fica no próprio accounts, que já está personificado.
 *
 * `entrarComo` precisa ter rodado antes: é o token que ele acabou de gravar
 * que autentica este pedido, e é dele que sai a empresa da sessão nova.
 */
export async function urlDoAdminPersonificado() {
    try {
        const resposta = await apiRequest({
            client: window.API_AUTH,
            method: 'post',
            url: '/sso/ticket',
            body: { app: SLUG_ADMIN },
        });

        if (!resposta?.ticket || !resposta?.redirect_url) {
            return null;
        }

        return `${resposta.redirect_url}#k=${encodeURIComponent(resposta.ticket)}`;
    } catch {
        return null;
    }
}

/**
 * Navega para o admin, passando pelo ticket de SSO quando ha personificacao
 * ativa.
 *
 * Sem isto, "Voltar para o Admin" era um `<a href>` estatico: o admin abria
 * com o token que ja estava no localStorage dele -- o de quem personificou,
 * aberto antes de a personificacao comecar -- e a identidade assumida ficava
 * presa no accounts. Dali em diante, qualquer app aberto pelo menu do admin
 * herdava o token errado.
 *
 * Sem personificacao, navega direto: nao ha por que pagar a ida extra ao
 * servidor quando o link estatico ja resolve certo.
 */
export async function irParaAdmin(linkEstatico) {
    if (!personificando()) {
        window.location.href = linkEstatico;
        return;
    }

    const url = await urlDoAdminPersonificado();
    window.location.href = url || linkEstatico;
}

/** Volta para a conta de quem personificou. */
export async function voltarParaMim() {
    const resposta = await apiRequest({
        client: window.API_AUTH,
        method: 'post',
        url: 'despersonificar',
    });

    aplicar(resposta);
    localStorage.removeItem(CHAVE_ATOR);

    await atualizarPermissoes();

    return resposta;
}

function aplicar(resposta) {
    setAuth({
        token: resposta?.access_token,
        credentials: resposta?.credentials,
    });
}

/**
 * Recarrega os caches de permissao ('permissions' e 'permissionsApi') para a
 * identidade que acabou de assumir.
 *
 * Personificar/despersonificar so trocam o token e as credentials -- o menu
 * lateral e os `podeAgora(...)` das telas leem os dois caches do
 * localStorage, nao o token. Sem isto, quem personifica um usuario com MENOS
 * acesso continua vendo o menu de quem personificou ate atualizar a pagina
 * inteira (a API ja barra certo por baixo, mas a interface mente).
 *
 * A chamada as vezes esbarra num 401 passageiro logo apos o token trocar --
 * visto ao vivo em dev, e o mesmo token que falhou aqui funcionou normalmente
 * poucos segundos depois. A causa raiz nao foi encontrada (nao e clock skew
 * entre replicas do auth -- JWT_LEEWAY nao mudou o comportamento; ver
 * docs/pendencias.md). Na duvida, tenta um punhado de vezes com espera
 * crescente antes de desistir.
 *
 * Se todas falharem, `marcarPermissaoDesconfiavel()` avisa `pode()` e o
 * `LeftSidebar` para pararem de confiar no cache que sobrou -- que e' o da
 * identidade ANTERIOR. Zerar o cache (escrever `codes: []` etc direto) foi
 * tentado antes disso e revertido: ver docs/pendencias.md sobre por que um
 * "cache vazio conhecido" nao da para forjar com o contrato atual de
 * `pode()` (colide com o fail-open deliberado para catalogo vazio).
 */
async function atualizarPermissoes() {
    const esperas = [800, 1600, 3200];

    for (let tentativa = 0; ; tentativa++) {
        try {
            await useAuthStore().fetchPermissions();
            return;
        } catch (erro) {
            if (tentativa >= esperas.length) {
                console.error('Nao foi possivel atualizar as permissoes apos trocar de identidade:', erro);
                marcarPermissaoDesconfiavel();
                return;
            }
            await new Promise((resolve) => setTimeout(resolve, esperas[tentativa]));
        }
    }
}

/**
 * Le o claim `act` do token, sem validar assinatura.
 *
 * Ler aqui e seguro porque o valor so decide o que a TELA mostra. Quem
 * autoriza de verdade e o backend, que confere o token assinado -- se alguem
 * forjar este campo no navegador, ganha uma faixa de aviso mentirosa e nada
 * mais.
 */
function lerAtorDoToken(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload?.act ?? null;
    } catch {
        return null;
    }
}
